import { Component, HostBinding, Input, OnInit } from '@angular/core';
import { PanelBarExpandMode } from '@progress/kendo-angular-layout';
import { AccountConfigService } from '../../../services/account-config/account-config.service';
import { SessionService } from '../../../services/session/session.service';
import { MAmzone, MDocument, MSession } from '@argalis-apps/models';
import { DocumentService } from '../../../services/document/document.service';
import { map, switchMap } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { animate, style, transition, trigger } from '@angular/animations';

@Component({
  selector: 'ags-session-evaluation',
  templateUrl: './session-evaluation.component.html',
  styleUrls: ['./session-evaluation.component.scss'],
  animations: [
    trigger('alertDisplay', [
      transition(':enter', [
        style({ transform: 'scale(0.8)', opacity: '0', height: '0' }),
        animate(100, style({ height: '100%' })),
        animate(300, style({ transform: 'scale(1)', opacity: '1' }))
      ]),
      transition(':leave', [
        animate(300, style({ transform: 'scale(0.5)', opacity: '0' })),
        animate(100, style({ height: '0' }))
      ])
    ])
  ]
})
export class SessionEvaluationComponent implements OnInit {
  @HostBinding('class') themeFlag: string;
  public expandMode: number = PanelBarExpandMode.Single;
  public infoEvaluation: MSession.IEvaluation[] = [] as MSession.IEvaluation[];
  public choosedCompetenceToAddMotif: MSession.ISessionApprenantCompetences;
  public satisfactions = ['acquis_peu_satisfaisant', 'acquis_satisfaisant', 'acquis_tres_satisfaisant'];
  public openedMotifiDialog = false;
  public titleDialog = '';
  public valueMotif = '';
  public idSession;
  public loader = true;
  public selectSessionApprenantid;
  public errorServer = false;
  @Input('idSession')
  set _idSession(value: string) {
    if (value) {
      this.idSession = value;
    }
  }
  constructor(
    private accountConfigService: AccountConfigService,
    private sessionService: SessionService,
    private documentService: DocumentService
  ) {}

  ngOnInit() {
    this.themeFlag = `${this.themeFlag || ''} ${this.accountConfigService.themingClassName}`;
    this.getEvaluationInfo(this.idSession);
  }

  getEvaluationInfo(idSession) {
    this.infoEvaluation = [] as MSession.IEvaluation[];
    const tableLinked = 'session_apprenant_competence_document';
  
    this.sessionService
      .getEvaluationInfo(idSession)
      .pipe(
        switchMap(infoEvaluation => {
          if (!infoEvaluation.length) {
            this.loader = false;
            return [];
          }
  
          this.selectSessionApprenantid = infoEvaluation[0].id_session_apprenant;
          const docRequests = infoEvaluation.map((apprenantInfo, index) => 
            this.documentService
              .getDocumentPresigned(apprenantInfo.id_session_apprenant, tableLinked)
              .pipe(
                map(docInfo => this.initInfoEvaluation(apprenantInfo, docInfo))
              )
          );
  
          return forkJoin(docRequests);
        })
      )
      .subscribe(
        evaluations => {
          this.infoEvaluation = evaluations;
          this.loader = false;
        },
        error => {
          console.error(error);
          this.loader = false;
        }
      );
  }

  initInfoEvaluation(
    evaluation: MSession.IEvaluation,
    docInfo: MDocument.IDocumentPresignedResponse
  ): MSession.IEvaluation {
    return {
      nom: evaluation.nom,
      id_session_apprenant: evaluation.id_session_apprenant,
      evaluation_finale_satisfaisant: evaluation.evaluation_finale_satisfaisant,
      evaluation_finale_peu_satisfaisant: evaluation.evaluation_finale_peu_satisfaisant,
      evaluation_finale_tres_satisfaisant: evaluation.evaluation_finale_tres_satisfaisant,
      sessionApprenantCompetences: evaluation.sessionApprenantCompetences,
      status: [false, false, false],
      document: docInfo
    };
  }

  // lorsque l'utilisateur coche une case de satisfaction multiple,
  // on ajuste la valeur des différentes cases de la grille:
  // (1) si on est sur la même colonne, on réplique la valeur de la case multiple
  // (2) si on est sur une autre colonne, et que la case multiple est checkée, on décoche
  toggleSatisfactionStatus(i, evalutionOfApprenant: MSession.IEvaluation) {
    this.infoEvaluation.forEach(item => {
      if (item === evalutionOfApprenant) {
        const column = this.satisfactions[i];
        const checked = !item.status[i];
        for (const competence of item.sessionApprenantCompetences || []) {
          for (const satisfaction of this.satisfactions) {
            if (satisfaction === column) {
              if (satisfaction === 'acquis_tres_satisfaisant') {
                competence[satisfaction] = checked;
                competence.acquis_peu_satisfaisant = false;
                competence.acquis_satisfaisant = false;
              } else if (satisfaction === 'acquis_peu_satisfaisant') {
                competence[satisfaction] = checked;
                competence.acquis_satisfaisant = false;
                competence.acquis_tres_satisfaisant = false;
              } else if (satisfaction === 'acquis_satisfaisant') {
                competence[satisfaction] = checked;
                competence.acquis_peu_satisfaisant = false;
                competence.acquis_tres_satisfaisant = false;
              }
              this.updateCompetenceInfo(competence);
            }
          }
        }
        item.status = [false, false, false];
        item.status[i] = checked;
      }
    });
  }
  satisfactionChanged(
    evalutionOfApprenant: MSession.IEvaluation,
    competence: MSession.ISessionApprenantCompetences,
    i: number
  ) {
    this.infoEvaluation.forEach(item => {
      item.status = [false, false, false];
      if (item === evalutionOfApprenant) {
        item.sessionApprenantCompetences.forEach(comp => {
          if (comp === competence) {
            switch (i) {
              case 0:
                comp.acquis_peu_satisfaisant = !comp.acquis_peu_satisfaisant;
                comp.acquis_satisfaisant = false;
                comp.acquis_tres_satisfaisant = false;
                break;
              case 1:
                comp.acquis_peu_satisfaisant = false;
                comp.acquis_satisfaisant = !comp.acquis_satisfaisant;
                comp.acquis_tres_satisfaisant = false;
                break;
              case 2:
                comp.acquis_peu_satisfaisant = false;
                comp.acquis_satisfaisant = false;
                comp.acquis_tres_satisfaisant = !comp.acquis_tres_satisfaisant;
                break;
            }
            this.updateCompetenceInfo(comp);
          }
        });
      }
    });
  }
  evaluationFinaleChanged(apprenantInfo, i) {
    this.infoEvaluation.forEach(evaluation => {
      if (apprenantInfo === evaluation) {
        evaluation.evaluation_finale_peu_satisfaisant = i === 0;
        evaluation.evaluation_finale_satisfaisant = i === 1;
        evaluation.evaluation_finale_tres_satisfaisant = i === 2;
        this.updateEvaluation(evaluation);
      }
    });
  }
  updateCompetenceInfo(competenceInfo: MSession.ISessionApprenantCompetences) {
    this.sessionService.updateCompetenceInfo(competenceInfo).subscribe(
      () => {},
      () => {
        console.error(`${new Date().toISOString()} error`);
      }
    );
  }

  updateEvaluation(evaluation: MSession.IEvaluation) {
    this.sessionService.updateEvaluationInfo(evaluation).subscribe(
      res => {
        console.log(`${new Date().toISOString()} res`, res);
      },
      () => {
        console.error(`${new Date().toISOString()} error`);
      }
    );
  }

  closeMotifDialog() {
    this.openedMotifiDialog = false;
  }

  openDialog(competence: MSession.ISessionApprenantCompetences) {
    this.valueMotif = competence.motif || '';
    this.titleDialog = 'Motif invalidation : ' + competence.competence_libelle || '';
    this.openedMotifiDialog = true;
    this.choosedCompetenceToAddMotif = competence;
  }

  addMotif() {
    this.updateCompetenceInfo(this.choosedCompetenceToAddMotif);
    this.getEvaluationInfo(this.idSession);
    this.openedMotifiDialog = false;
  }

  changeMotif(event: string) {
    this.choosedCompetenceToAddMotif.motif = event;
  }

  uploadInfo(amazoneResponse: MAmzone.IAmazoneUploadReponse) {
    this.errorServer = false;
    if (amazoneResponse) {
      const tableLinked = 'session_apprenant_competence_document';
      this.documentService.insertDocument(tableLinked, this.selectSessionApprenantid, amazoneResponse).subscribe(
        () => {
          this.getEvaluationInfo(this.idSession);
        },
        () => {
          this.errorServer = true;
        }
      );
    }
  }

  changeITem($event: any) {
    if (event) {
      const idSessionApprenant = (((event as any).path[1] as HTMLElement).id || '').split('-').splice(-1)[0];
      if (idSessionApprenant) {
        this.selectSessionApprenantid = idSessionApprenant;
      }
    }
  }
}
